// Диспетчер побочных эффектов, общий для createQuery и createMutation.

import { HttpError } from '../http/HttpError'
import type {
  CommonSideEffects,
  DisabledSideEffectsConfig,
  MutationSideEffects,
  QueryContext,
  SideEffectKey,
} from './types'

/**
 * `disabledSideEffects` в рантайм-опциях отключает эффекты, объявленные при СОЗДАНИИ фабрики.
 * Эффекты, переданные в рантайме, выполняются всегда.
 */
export function createShouldRunSideEffect(
  disabledSideEffects: DisabledSideEffectsConfig | undefined,
): (effectName: SideEffectKey) => boolean {
  return (effectName) => {
    if (!disabledSideEffects) return true
    if (disabledSideEffects === true || disabledSideEffects === 'all') return false
    if (Array.isArray(disabledSideEffects)) return !disabledSideEffects.includes(effectName)
    return true
  }
}

/** Запускает onErrorWithCode / onHttpError / onAnyError — объявленные и рантайм-версии */
export function runErrorSideEffects<TResult, TInput, TContext extends object>(
  error: unknown,
  ctx: QueryContext<TInput, TContext>,
  declared: CommonSideEffects<TResult, TInput, TContext> | undefined,
  runtime: CommonSideEffects<TResult, TInput, TContext> | undefined,
  shouldRun: (name: SideEffectKey) => boolean,
): void {
  const httpError = error instanceof HttpError ? error : null

  if (httpError?.code) {
    if (shouldRun('onErrorWithCode')) declared?.onErrorWithCode?.(httpError.code, ctx)
    runtime?.onErrorWithCode?.(httpError.code, ctx)
  }

  if (httpError) {
    if (shouldRun('onHttpError')) declared?.onHttpError?.(httpError, ctx)
    runtime?.onHttpError?.(httpError, ctx)
  }

  if (shouldRun('onAnyError')) declared?.onAnyError?.(error, ctx)
  runtime?.onAnyError?.(error, ctx)
}

/** Запускает onSuccess — объявленный и рантайм-версию */
export function runSuccessSideEffects<TResult, TInput, TContext extends object>(
  data: TResult,
  ctx: QueryContext<TInput, TContext>,
  declared: CommonSideEffects<TResult, TInput, TContext> | undefined,
  runtime: CommonSideEffects<TResult, TInput, TContext> | undefined,
  shouldRun: (name: SideEffectKey) => boolean,
): void {
  if (shouldRun('onSuccess')) declared?.onSuccess?.(data, ctx)
  runtime?.onSuccess?.(data, ctx)
}

/** Инвалидирует запросы, перечисленные в `invalidateQueries` — только для мутаций */
export async function runInvalidateQueries<TResult, TInput, TContext extends object>(
  ctx: QueryContext<TInput, TContext>,
  declared: MutationSideEffects<TResult, TInput, TContext> | undefined,
  runtime: MutationSideEffects<TResult, TInput, TContext> | undefined,
  shouldRun: (name: SideEffectKey) => boolean,
): Promise<void> {
  const tasks: Promise<void>[] = []

  if (shouldRun('invalidateQueries')) {
    declared?.invalidateQueries?.forEach((query) => tasks.push(query.invalidate(ctx.queryClient)))
  }

  runtime?.invalidateQueries?.forEach((query) => tasks.push(query.invalidate(ctx.queryClient)))

  await Promise.all(tasks)
}
